
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRouter } from 'vue-router'

import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

import { companies } from '@/core/realisations/companies.realisation'
import { billing } from '@/core/realisations/billing.realisation'

import { DataState } from '@/core/classes/DataState.class'

import { CalculationT } from '@/core/types/billing.types'

import useNotifications from '@/common/composable/useNotifications'

import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        TheInput,
        TheSelect,
        Loader,
    },
    setup() {
        const router = useRouter()
        const notifications = useNotifications()

        const onRouteBack = () => {
            router.push('/billing')
        }

        const state = reactive({
            payerCompanyId: null as number | null,
            //
            is_calculations_loading: false,
            is_calculations_failed: false,
            calculations: [] as CalculationT[],
            calculations_stash: [] as CalculationT[],
            //
            is_loading: false,
            query: null as string | null,
        })

        const state_form$ = useVuelidate(
            {
                payerCompanyId: { required },
            },
            state
        )

        const data_state = reactive({
            companies: new DataState({}),
        })

        const loadCompanies = async () => {
            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({
                    page: 1,
                    limit: 9999,
                    billingEnabled: 1,
                    isExtended: 0,
                })
                data_state.companies.list = response.data

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadCalculations = async (payerCompanyId: number) => {
            if (!payerCompanyId) {
                notifications.notify({ title: 'Выберите контрагента', type: 'danger' })
                return
            }

            try {
                state.is_calculations_loading = true

                const response = await billing.getCalculations({
                    payerCompanyId,
                    page: 1,
                    limit: 9999,
                    isLastTwoMonth: 1,
                })
                state.calculations_stash = response.data

                state.is_calculations_failed = false
            } catch (error) {
                console.error(error)
                state.is_calculations_failed = true
            } finally {
                state.is_calculations_loading = false
            }
        }

        onBeforeMount(loadCompanies)

        const renderedCalculations = computed(() => {
            if (state.query) {
                return state.calculations_stash.filter((calc) => {
                    return calc.invoiceNumber
                        .toLowerCase()
                        .includes((state.query as string).toLowerCase())
                })
            } else {
                return state.calculations_stash
            }
        })

        const onRemoveCalculation = (calculation: CalculationT) => {
            const calculation_index = state.calculations.findIndex(
                (calc) => calc.id === calculation.id
            )
            if (calculation_index < 0) return

            state.calculations.splice(calculation_index, 1)
            state.calculations_stash.unshift({ ...calculation })
        }

        const onAddCalculation = (calculation: CalculationT) => {
            const calculation_index = state.calculations_stash.findIndex(
                (calc) => calc.id === calculation.id
            )
            if (calculation_index < 0) return

            state.calculations.push({ ...calculation })
            state.calculations_stash.splice(calculation_index, 1)
        }

        const addAll = () => {
            state.calculations_stash.forEach((calculation) => {
                state.calculations.push({ ...calculation })
            })
            state.calculations_stash = []
        }

        const isCalculationAlreadyAdded = (calculation_id: number) => {
            const calculation = state.calculations.find((calc) => calc.id === calculation_id)
            return Boolean(calculation)
        }

        const resetState = () => {
            notifications.notify({ title: 'Вычисления успешно обновлены!', type: 'success' })

            state.payerCompanyId = null
            state.calculations = []
            state.calculations_stash = []
            state_form$.value.$reset()
        }

        const onSubmit = async () => {
            if (state.is_loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_loading = true

                const calculationIds = state.calculations.map((calc) => calc.id)

                await billing.updateFewCalculations(calculationIds)
                resetState()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При обновлении вычислений произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        return {
            onRouteBack,
            state,
            state_form$,
            data_state,
            loadCompanies,
            loadCalculations,
            renderedCalculations,
            onRemoveCalculation,
            onAddCalculation,
            isCalculationAlreadyAdded,
            onSubmit,
            addAll,
        }
    },
})
